(() => {
  if (customElements.get('embedded-reviews-modal')) return;

  const STAR_FULL =
    '<svg viewBox="0 0 20 20" class="is-filled" aria-hidden="true"><path d="M10 1.5l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L1.3 7.8l6.1-.7z"/></svg>';
  const STAR_EMPTY =
    '<svg viewBox="0 0 20 20" class="is-empty" aria-hidden="true"><path d="M10 1.5l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L1.3 7.8l6.1-.7z"/></svg>';

  const cache = {};

  const abbreviate = (num) => {
    num = Number(num) || 0;
    if (num < 1000) return String(num);
    if (num < 1000000) return `${(num / 1000).toFixed(num % 1000 >= 100 ? 1 : 0)}k`;
    return `${(num / 1000000).toFixed(1)}m`;
  };

  const starsMarkup = (score) => {
    const rounded = Math.round(Number(score) || 0);
    let html = '';
    for (let i = 1; i <= 5; i++) html += i <= rounded ? STAR_FULL : STAR_EMPTY;
    return `<span class="erm-stars">${html}</span>`;
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
    ));

  const timeAgo = (isoDate, i18n) => {
    const then = new Date(isoDate).getTime();
    if (Number.isNaN(then)) return '';
    const diffSeconds = Math.max(0, (Date.now() - then) / 1000);
    const units = [
      ['year', 31536000],
      ['month', 2592000],
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
    ];
    for (const [label, seconds] of units) {
      const value = Math.floor(diffSeconds / seconds);
      if (value >= 1) {
        const template = i18n[`${label}Ago${value > 1 ? 'Other' : 'One'}`];
        return template.replace('%d', value);
      }
    }
    return i18n.justNow;
  };

  async function fetchReviews(apiKey, productId, page, perPage) {
    const url = `https://api-cdn.yotpo.com/v1/widget/${apiKey}/products/${productId}/reviews.json?page=${page}&per_page=${perPage}&sort=date&direction=desc`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Yotpo reviews request failed: ${res.status}`);
    const json = await res.json();
    return json.response;
  }

  function getEntry(apiKey, productId) {
    if (!cache[productId]) {
      cache[productId] = {
        bottomline: null,
        reviews: [],
        page: 0,
        perPage: 10,
        hasMore: true,
        loading: null,
      };
    }
    const entry = cache[productId];
    if (!entry.loading && entry.page === 0) {
      entry.loading = loadNextPage(apiKey, productId);
    }
    return entry;
  }

  async function loadNextPage(apiKey, productId) {
    const entry = cache[productId];
    const nextPage = entry.page + 1;
    try {
      const response = await fetchReviews(apiKey, productId, nextPage, entry.perPage);
      entry.bottomline = response.bottomline;
      entry.lastFetchedCount = (response.reviews || []).length;
      entry.reviews = entry.reviews.concat(response.reviews || []);
      entry.page = nextPage;
      entry.hasMore = entry.reviews.length < (response.bottomline?.total_review || 0);
    } finally {
      entry.loading = null;
    }
    return entry;
  }

  function renderTrigger(trigger, bottomline) {
    if (!bottomline || !bottomline.total_review) {
      trigger.innerHTML = '';
      return;
    }
    trigger.innerHTML = `
      ${starsMarkup(bottomline.average_score)}
      <span class="erm-summary__count">(${bottomline.total_review})</span>
    `;
    trigger.classList.add('erm-trigger--ready');
  }

  function renderBars(bottomline) {
    const distribution = bottomline.star_distribution || {};
    const total = bottomline.total_review || 0;
    return [5, 4, 3, 2, 1]
      .map((stars) => {
        const count = Number(distribution[stars]) || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return `
          <div class="erm-bar">
            <span>${stars} ★</span>
            <span class="erm-bar__track"><span class="erm-bar__fill" style="width:${pct}%"></span></span>
            <span class="erm-bar__count">${abbreviate(count)}</span>
          </div>
        `;
      })
      .join('');
  }

  // Yotpo keys custom_fields by form field id. Only Rating fields (1-5) are shown: the Size field
  // comes as a numeric code without a label.
  function renderReviewFields(review) {
    const ratings = Object.values(review.custom_fields || {}).filter(
      (field) => field && field.field_type === 'Rating' && field.title && Number(field.value) > 0
    );
    if (!ratings.length) return '';
    return `
      <div class="erm-review__fields">
        ${ratings
          .map(
            (field) => `
              <div class="erm-review__field">
                <span class="erm-review__field-label">${escapeHtml(field.title)}</span>
                ${starsMarkup(field.value)}
              </div>
            `
          )
          .join('')}
      </div>
    `;
  }

  function renderReview(review, i18n) {
    const name = review.user?.display_name || i18n.anonymous;
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    const title = review.title;
    const body = review.content;
    return `
      <div class="erm-review">
        <div class="erm-review__header">
          <span class="erm-review__avatar">${escapeHtml(initial)}</span>
          <span class="erm-review__name">${escapeHtml(name)}</span>
          ${review.verified_buyer ? `<span class="erm-review__verified">${escapeHtml(i18n.verifiedBuyer)}</span>` : ''}
        </div>
        <div class="erm-review__meta">
          ${starsMarkup(review.score)}
          <span class="erm-review__date">${timeAgo(review.created_at, i18n)}</span>
        </div>
        ${title ? `<p class="erm-review__title">${escapeHtml(title)}</p>` : ''}
        ${body ? `<p class="erm-review__body">${escapeHtml(body)}</p>` : ''}
        ${renderReviewFields(review)}
      </div>
    `;
  }

  class EmbeddedReviewsModal extends HTMLElement {
    connectedCallback() {
      // Move to <body> so `position: fixed` is always relative to the viewport, even
      // when this element is nested inside an ancestor with a transform/will-change
      // (e.g. a sticky add-to-cart bar), which would otherwise turn "fixed" into
      // "fixed relative to that ancestor" and clip/hide the overlay.
      if (!this._portaled && this.parentElement !== document.body) {
        this._portaled = true;
        document.body.appendChild(this);
        return;
      }

      this.apiKey = this.dataset.apiKey;
      this.i18n = {
        loading: this.dataset.i18nLoading,
        loadError: this.dataset.i18nLoadError,
        noReviews: this.dataset.i18nNoReviews,
        title: this.dataset.i18nTitle,
        basedOnReviewsOne: this.dataset.i18nBasedOnReviewsOne,
        basedOnReviewsOther: this.dataset.i18nBasedOnReviewsOther,
        loadMore: this.dataset.i18nLoadMore,
        loadingMore: this.dataset.i18nLoadingMore,
        loadMoreError: this.dataset.i18nLoadMoreError,
        anonymous: this.dataset.i18nAnonymous,
        verifiedBuyer: this.dataset.i18nVerifiedBuyer,
        justNow: this.dataset.i18nJustNow,
        yearAgoOne: this.dataset.i18nYearAgoOne,
        yearAgoOther: this.dataset.i18nYearAgoOther,
        monthAgoOne: this.dataset.i18nMonthAgoOne,
        monthAgoOther: this.dataset.i18nMonthAgoOther,
        dayAgoOne: this.dataset.i18nDayAgoOne,
        dayAgoOther: this.dataset.i18nDayAgoOther,
        hourAgoOne: this.dataset.i18nHourAgoOne,
        hourAgoOther: this.dataset.i18nHourAgoOther,
        minuteAgoOne: this.dataset.i18nMinuteAgoOne,
        minuteAgoOther: this.dataset.i18nMinuteAgoOther,
      };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-erm-close]')) this.close();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.hidden) this.close();
      });
      this.addEventListener('click', (event) => {
        const loadMoreBtn = event.target.closest('[data-erm-load-more]');
        if (loadMoreBtn) this.loadMore(loadMoreBtn);
      });
    }

    async open(productId) {
      this.productId = productId;
      this.hidden = false;
      document.body.classList.add('erm-scroll-lock');
      this.querySelector('.embedded-reviews-modal__panel')?.focus();
      const entry = getEntry(this.apiKey, productId);
      if (entry.loading) this.showStatus(this.i18n.loading);
      try {
        await entry.loading;
        if (this.productId === productId) this.renderBody(entry);
      } catch (err) {
        console.error(err);
        this.showStatus(this.i18n.loadError);
      }
    }

    close() {
      this.hidden = true;
      document.body.classList.remove('erm-scroll-lock');
    }

    showStatus(message) {
      const body = this.querySelector('[data-erm-body]');
      if (body) body.innerHTML = `<p class="embedded-reviews-modal__status">${escapeHtml(message)}</p>`;
    }

    renderBody(entry) {
      const body = this.querySelector('[data-erm-body]');
      if (!body) return;
      const { bottomline, reviews, hasMore } = entry;
      if (!bottomline || !bottomline.total_review) {
        this.showStatus(this.i18n.noReviews);
        return;
      }
      const reviewCountLabel = (
        bottomline.total_review > 1 ? this.i18n.basedOnReviewsOther : this.i18n.basedOnReviewsOne
      ).replace('%d', bottomline.total_review);
      body.innerHTML = `
        <h2 class="erm-title">${escapeHtml(this.i18n.title)}</h2>
        <div class="erm-summary">
          <span class="erm-summary__score">${Number(bottomline.average_score).toFixed(1)}</span>
          ${starsMarkup(bottomline.average_score)}
          <span class="erm-summary__count">${escapeHtml(reviewCountLabel)}</span>
        </div>
        <div class="erm-bars">${renderBars(bottomline)}</div>
        <hr class="erm-divider">
        <div data-erm-list>${reviews.map((review) => renderReview(review, this.i18n)).join('')}</div>
        ${hasMore ? `<button type="button" class="erm-load-more" data-erm-load-more>${escapeHtml(this.i18n.loadMore)}</button>` : ''}
      `;
    }

    async loadMore(button) {
      button.disabled = true;
      button.textContent = this.i18n.loadingMore;
      try {
        const entry = await loadNextPage(this.apiKey, this.productId);
        const list = this.querySelector('[data-erm-list]');
        if (list) {
          list.insertAdjacentHTML(
            'beforeend',
            entry.reviews.slice(-entry.lastFetchedCount).map((review) => renderReview(review, this.i18n)).join('')
          );
        }
        if (entry.hasMore) {
          button.disabled = false;
          button.textContent = this.i18n.loadMore;
        } else {
          button.remove();
        }
      } catch (err) {
        console.error(err);
        button.textContent = this.i18n.loadMoreError;
      }
    }
  }

  customElements.define('embedded-reviews-modal', EmbeddedReviewsModal);

  function paintTrigger(trigger, apiKey) {
    const productId = trigger.dataset.productId;
    const entry = getEntry(apiKey, productId);
    Promise.resolve(entry.loading)
      .then(() => {
        if (trigger.dataset.productId === productId && entry.bottomline) renderTrigger(trigger, entry.bottomline);
      })
      .catch(() => {});
  }

  function initTrigger(trigger) {
    const targetId = trigger.dataset.embeddedReviewsTarget;
    const modal = targetId ? document.getElementById(targetId) : null;
    if (!trigger.dataset.productId || !modal) return;

    trigger.setAttribute('role', 'button');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', targetId);

    // The trigger's data-product-id is the only source of the product: embedded PDPs swap the
    // product in place by updating it (fit-color-picker*.js replaceReview) and stars and modal follow.
    const repaint = () => paintTrigger(trigger, modal.dataset.apiKey);
    repaint();
    new MutationObserver(repaint).observe(trigger, { attributes: true, attributeFilter: ['data-product-id'] });

    const openModal = () => modal.open(trigger.dataset.productId);
    trigger.addEventListener('click', openModal);
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openModal();
      }
    });
  }

  const initAll = (root) => root.querySelectorAll('[data-embedded-reviews-trigger]:not(.erm-trigger--bound)').forEach((trigger) => {
    trigger.classList.add('erm-trigger--bound');
    initTrigger(trigger);
  });

  initAll(document);

  // Some embedded-PDP sections re-render their markup (e.g. color/fit swap) without a
  // full page reload, which would otherwise orphan the trigger's listeners.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-embedded-reviews-trigger]')) initAll(node.parentNode || document);
        else if (node.querySelector?.('[data-embedded-reviews-trigger]')) initAll(node);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
